import { useEffect, useState } from 'react';
import { fipe } from '../api/fipe';

// Componente de campos de veículo com selects encadeados da API FIPE.
// Props:
//   value: { placa, marca, modelo, ano, cor, km, combustivel }
//   onChange: (novoValor) => void
//   compact: bool — layout menor para uso dentro de lista

export default function VeiculoFormFields({ value, onChange, compact = false }) {
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [anos, setAnos] = useState([]);

  const [marcaId, setMarcaId] = useState('');
  const [modeloId, setModeloId] = useState('');

  const [loadingMarcas, setLoadingMarcas] = useState(false);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [loadingAnos, setLoadingAnos] = useState(false);
  const [erroFipe, setErroFipe] = useState(false);

  const input = compact
    ? 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
    : 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  const label = compact
    ? 'block text-xs font-medium text-gray-600 mb-1'
    : 'block text-sm font-medium text-gray-700 mb-1';

  // Carrega marcas ao montar
  useEffect(() => {
    setLoadingMarcas(true);
    setErroFipe(false);
    fipe.marcas()
      .then(data => setMarcas(data.sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setErroFipe(true))
      .finally(() => setLoadingMarcas(false));
  }, []);

  // Carrega modelos quando marca muda
  useEffect(() => {
    if (!marcaId) { setModelos([]); setModeloId(''); setAnos([]); return; }
    setLoadingModelos(true);
    setModelos([]); setModeloId(''); setAnos([]);
    onChange({ ...value, modelo: '', ano: '' });
    fipe.modelos(marcaId)
      .then(data => setModelos(data.sort((a, b) => a.name.localeCompare(b.name))))
      .finally(() => setLoadingModelos(false));
  }, [marcaId]);

  // Carrega anos quando modelo muda
  useEffect(() => {
    if (!marcaId || !modeloId) { setAnos([]); return; }
    setLoadingAnos(true);
    setAnos([]);
    onChange({ ...value, ano: '' });
    fipe.anos(marcaId, modeloId)
      .then(data => setAnos(data))
      .finally(() => setLoadingAnos(false));
  }, [modeloId]);

  const selecionarMarca = e => {
    const id = e.target.value;
    const nome = marcas.find(m => String(m.code) === id)?.name || '';
    setMarcaId(id);
    setModeloId('');
    onChange({ ...value, marca: nome, modelo: '', ano: '' });
  };

  const selecionarModelo = e => {
    const id = e.target.value;
    const nome = modelos.find(m => String(m.code) === id)?.name || '';
    setModeloId(id);
    onChange({ ...value, modelo: nome, ano: '' });
  };

  const selecionarAno = e => {
    // O "ano" da FIPE vem como "2020-1" — pegamos só o ano numérico
    const raw = e.target.value;
    const anoNum = raw ? parseInt(raw.split('-')[0]) : '';
    onChange({ ...value, ano: anoNum });
  };

  // Monta o option value do ano selecionado
  const anoSelecionado = anos.find(a => parseInt(a.name?.split(' ')[0]) === Number(value.ano))?.code || '';

  return (
    <div className="space-y-3">
      {erroFipe && (
        <div className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
          ⚠️ Não foi possível carregar dados da FIPE. Preencha manualmente.
        </div>
      )}

      {/* Placa */}
      <div>
        <label className={label}>Placa</label>
        <input
          className={input + ' uppercase'}
          placeholder="ABC1D23"
          value={value.placa}
          onChange={e => onChange({ ...value, placa: e.target.value.toUpperCase() })}
        />
      </div>

      {/* Marca — select FIPE ou input manual se FIPE falhou */}
      <div>
        <label className={label}>Marca</label>
        {!erroFipe ? (
          <select
            className={input}
            value={marcaId}
            onChange={selecionarMarca}
            disabled={loadingMarcas}
          >
            <option value="">{loadingMarcas ? 'Carregando marcas...' : 'Selecione a marca'}</option>
            {marcas.map(m => (
              <option key={m.code} value={m.code}>{m.name}</option>
            ))}
          </select>
        ) : (
          <input className={input} placeholder="Ex: Volkswagen" value={value.marca} onChange={e => onChange({ ...value, marca: e.target.value })} />
        )}
      </div>

      {/* Modelo */}
      <div>
        <label className={label}>Modelo</label>
        {!erroFipe ? (
          <select
            className={input}
            value={modeloId}
            onChange={selecionarModelo}
            disabled={!marcaId || loadingModelos}
          >
            <option value="">
              {!marcaId ? 'Selecione a marca primeiro' : loadingModelos ? 'Carregando modelos...' : 'Selecione o modelo'}
            </option>
            {modelos.map(m => (
              <option key={m.code} value={m.code}>{m.name}</option>
            ))}
          </select>
        ) : (
          <input className={input} placeholder="Ex: Gol" value={value.modelo} onChange={e => onChange({ ...value, modelo: e.target.value })} />
        )}
      </div>

      {/* Ano / Versão */}
      <div>
        <label className={label}>Ano / Versão</label>
        {!erroFipe ? (
          <select
            className={input}
            value={anoSelecionado}
            onChange={selecionarAno}
            disabled={!modeloId || loadingAnos}
          >
            <option value="">
              {!modeloId ? 'Selecione o modelo primeiro' : loadingAnos ? 'Carregando anos...' : 'Selecione o ano'}
            </option>
            {anos.map(a => (
              <option key={a.code} value={a.code}>{a.name}</option>
            ))}
          </select>
        ) : (
          <input type="number" className={input} placeholder={new Date().getFullYear()} value={value.ano} onChange={e => onChange({ ...value, ano: e.target.value })} />
        )}
      </div>

      {/* Cor, KM e Combustível */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Cor</label>
          <input className={input} placeholder="Prata" value={value.cor} onChange={e => onChange({ ...value, cor: e.target.value })} />
        </div>
        <div>
          <label className={label}>KM atual</label>
          <input type="number" className={input} placeholder="0" value={value.km} onChange={e => onChange({ ...value, km: e.target.value })} />
        </div>
        <div className="col-span-2">
          <label className={label}>Combustível</label>
          <select className={input} value={value.combustivel} onChange={e => onChange({ ...value, combustivel: e.target.value })}>
            <option value="flex">Flex</option>
            <option value="gasolina">Gasolina</option>
            <option value="etanol">Etanol</option>
            <option value="diesel">Diesel</option>
            <option value="eletrico">Elétrico</option>
            <option value="hibrido">Híbrido</option>
          </select>
        </div>
      </div>
    </div>
  );
}
